/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { CollectionsModule, MODULE_LAYOUTS } from '@coursera-web/cds-labs';

import { Button, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useRelatedProgramsData } from 'bundles/unified-description-page-common/contexts/RelatedProgramsDataContext';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import { useSpecializationDegreeCollection } from 'bundles/unified-description-page-common/providers/specialization/SpecializationDegreeCollectionDataProvider';
import type { ConsumerSpecializationPageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationPageQuery';
import { getDegreeDescriptionPageUrl } from 'bundles/unified-description-page-common/utils';
import { previewXdpAsADiscoveryToolVariant } from 'bundles/unified-description-page-common/utils/xdpAsADiscoveryToolUtils';

// eslint-disable-next-line @coursera-web/custom-rules/i18n-import-rule
import _t from 'i18n!nls/xdp';

type Props = {
  pageData: ConsumerSpecializationPageFieldsFragment;
};

const ProgramsFromRoleRenderer = ({ pageData }: Props) => {
  const trackV3Event = useTracker();
  const { data, loading, eventingRecommender } = useRelatedProgramsData();
  const { degrees } = useSpecializationDegreeCollection();
  const [currentFilterIndex, setCurrentFilterIndex] = useState(0);
  const courseraPlusData = useCourseraPlusOwnership();
  const experimentVariant = previewXdpAsADiscoveryToolVariant();
  const shouldDisplayCourseraPlusVariant = courseraPlusData?.isCourseraPlusEligibleProduct && experimentVariant === 'B';

  const filtersWithEntities: Array<{ filterName: string; id: string; entities: CollectionEntity[] }> = useMemo(() => {
    const allEntites = data?.collectionEntities.filter((entity) => entity.slug !== pageData.slug) ?? [];
    const proCertEntities = allEntites.filter((entity) => entity.productType === 'PROFESSIONAL CERTIFICATE');
    const s12nEntities = allEntites.filter((entity) => entity.productType === 'SPECIALIZATION');

    const final = [];

    if (allEntites.length) {
      final.push({ filterName: _t('Popular'), id: 'popular', entities: allEntites });
    }
    if (proCertEntities.length && s12nEntities.length) {
      final.push({ filterName: _t('Professional Certificates'), id: 'proCerts', entities: proCertEntities });
      final.push({ filterName: _t('Specializations'), id: 's12ns', entities: s12nEntities });
    }
    if (degrees.length) {
      const convertedDegrees: CollectionEntity[] = degrees.map((degree) => {
        const convertedPartners = degree.partnerEntities.map((partner) => ({
          id: partner.id,
          logo: partner.squareLogo ?? '',
          name: partner.name,
        }));
        const converted: CollectionEntity = {
          id: degree.id,
          name: degree.name,
          slug: degree.slug,
          link: getDegreeDescriptionPageUrl(degree.slug),
          partners: convertedPartners,
          partnerLogos: convertedPartners.map(({ logo }) => logo),
          imageUrl: degree.imageUrl ?? '',
          productType: 'DEGREE',
        };
        return converted;
      });
      final.push({ filterName: _t('Degrees'), id: 'degrees', entities: convertedDegrees });
    }

    return final;
  }, [data?.collectionEntities, degrees, pageData.slug]);

  const handleFilterChange = useCallback<(index: number) => void>(
    (index: number) => {
      setCurrentFilterIndex(index);
      trackV3Event('click_button', {
        button: {
          name: 'collections_filter_chip',
          linkName: filtersWithEntities[index].filterName,
        },
      });
    },
    [filtersWithEntities, trackV3Event]
  );

  const roleName = data?.roleName ?? pageData.domains?.[0].domainName;

  const title = useMemo(() => {
    if (roleName && shouldDisplayCourseraPlusVariant) {
      return _t('Dive into endless #{roleName} content with one subscription', { roleName });
    }
    if (roleName && !shouldDisplayCourseraPlusVariant) {
      return _t('Popular for #{roleName}', { roleName });
    }
    return _t('Popular');
  }, [roleName, shouldDisplayCourseraPlusVariant]);

  if (!filtersWithEntities.length) {
    return null;
  }

  const carouselCollection: CarouselCollection = {
    title,
    collectionId: data?.collectionId ?? '',
    entities: filtersWithEntities[currentFilterIndex].entities,
    collectionTrackingData: eventingRecommender
      ? {
          carouselRecommender: eventingRecommender,
        }
      : undefined,
  };

  if (shouldDisplayCourseraPlusVariant) {
    carouselCollection.renderCustomPrimaryCta = ({ size, fullWidth, variantOverride }) => (
      <Button
        size={size}
        fullWidth={fullWidth}
        component="a"
        variant={variantOverride ?? 'primary'}
        onClick={() => {
          trackV3Event('click_button', {
            button: {
              name: 'c_plus_learn_more',
            },
          });
        }}
        href="/courseraplus"
        css={
          fullWidth
            ? undefined
            : css`
                ${breakpoints.down('xs')} {
                  margin-right: var(--cds-spacing-200);
                }
              `
        }
      >
        {_t('Try for Free')}
      </Button>
    );
  }

  return (
    <TrackedDiv
      trackingName="recommendations_module"
      trackClicks={false}
      requireFullyVisible={true}
      withVisibilityTracking={true}
    >
      <CollectionsModule
        headline={title}
        isCollectionsLoading={loading}
        filters={filtersWithEntities.map(({ filterName, id }) => ({ filterName, id }))}
        onFilterChange={handleFilterChange}
        currentCollectionSetIndex={currentFilterIndex}
        collections={[carouselCollection]}
        layout={MODULE_LAYOUTS.textTopImageBottom}
      />
    </TrackedDiv>
  );
};

export default ProgramsFromRoleRenderer;
